/**
 * Cameroon Land Market - Administrator RBAC & Security Service
 * Strict Role-Based Access Control, Two-Factor Authentication (2FA),
 * Session Timeout Inactivity Watchdog, and Permission Boundary Enforcement.
 */

import { doc, getDoc, setDoc, updateDoc, collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { auth } from './firebase';
import { db } from './firestoreDb';
import { AdminRole, AdminUser } from '../types';
export {
  SUPER_ADMIN_EMAILS,
  LISTING_APPROVAL_ADMIN_EMAILS,
  isDesignatedAdminEmail,
  getDefaultRoleForEmail,
} from './adminConstants';
import {
  SUPER_ADMIN_EMAILS,
  LISTING_APPROVAL_ADMIN_EMAILS,
  isDesignatedAdminEmail,
  getDefaultRoleForEmail,
} from './adminConstants';

export type AdminResource =
  | 'overview'
  | 'listings'
  | 'verifications'
  | 'users'
  | 'moderation'
  | 'inquiries'
  | 'taxonomies'
  | 'content'
  | 'finance'
  | 'analytics'
  | 'backups'
  | 'audit_logs'
  | 'admins'
  | 'approvals';

export type AdminAction =
  | 'view'
  | 'create'
  | 'edit'
  | 'approve'
  | 'export'
  | 'suspend'
  | 'delete'
  | 'restore';

/**
 * Explicit RBAC Permission Matrix for Cameroon Land Market
 * Deny by default: Any action/resource not explicitly permitted is blocked.
 * Backup Admin CANNOT access user private documents or listing moderation.
 * Operations Admin CANNOT moderate listings or access private title documents.
 */
export const ADMIN_PERMISSION_MATRIX: Record<
  AdminRole,
  Partial<Record<AdminResource, AdminAction[]>>
> = {
  'Super Admin': {
    overview: ['view', 'export'],
    listings: ['view', 'create', 'edit', 'approve', 'export', 'suspend', 'delete', 'restore'],
    verifications: ['view', 'create', 'edit', 'approve', 'export'],
    users: ['view', 'create', 'edit', 'export', 'suspend', 'delete', 'restore'],
    moderation: ['view', 'edit', 'approve', 'export', 'delete'],
    inquiries: ['view', 'create', 'edit', 'export', 'delete'],
    taxonomies: ['view', 'create', 'edit', 'delete'],
    content: ['view', 'create', 'edit', 'delete'],
    finance: ['view', 'export'],
    analytics: ['view', 'export'],
    backups: ['view', 'create', 'export', 'restore', 'delete'],
    admins: ['view', 'create', 'edit', 'suspend', 'delete'],
    approvals: ['view', 'create', 'approve', 'delete'],
    audit_logs: ['view', 'export'],
  },
  'Listing Approval Admin': {
    overview: ['view', 'export'],
    listings: ['view', 'create', 'edit', 'approve', 'export', 'suspend', 'restore'],
    verifications: ['view', 'edit', 'approve', 'export'],
    moderation: ['view', 'edit', 'approve'],
    inquiries: ['view'],
    taxonomies: ['view'],
    content: ['view', 'edit'],
    analytics: ['view'],
    audit_logs: ['view'],
  },
  'Operations Admin': {
    overview: ['view', 'export'],
    users: ['view', 'create', 'edit', 'export', 'suspend', 'restore'],
    inquiries: ['view', 'create', 'edit', 'export'],
    moderation: ['view', 'edit', 'approve', 'export'],
    taxonomies: ['view', 'create', 'edit', 'delete'],
    content: ['view', 'create', 'edit'],
    analytics: ['view', 'export'],
    audit_logs: ['view'],
  },
  'Backup Admin': {
    overview: ['view'],
    backups: ['view', 'create', 'export', 'restore'],
    approvals: ['view', 'create', 'approve'],
    analytics: ['view'],
    audit_logs: ['view'],
  },
};

/**
 * Permission Matrix Evaluator:
 * Evaluates whether an administrator role has permission to access a resource and action.
 * Denies access by default.
 */
export function hasAdminPermission(
  role: AdminRole | string | undefined,
  resource: AdminResource | string,
  action: AdminAction = 'view'
): boolean {
  if (!role) return false;
  const normalizedRole = role as AdminRole;
  const permissions = ADMIN_PERMISSION_MATRIX[normalizedRole];
  if (!permissions) return false;
  const allowedActions = permissions[resource as AdminResource];
  if (!allowedActions) return false;
  return allowedActions.includes(action);
}

/**
 * Rule: An administrator CANNOT approve or edit their own property listings.
 */
export function canApproveProperty(
  role: AdminRole | string | undefined,
  propertyOwnerId?: string | null,
  currentAdminUid?: string | null
): { allowed: boolean; reason?: string } {
  if (!role) {
    return { allowed: false, reason: 'No administrator role detected.' };
  }

  // Prevent self-approval if the admin owns the listing
  if (propertyOwnerId && currentAdminUid && propertyOwnerId === currentAdminUid) {
    return {
      allowed: false,
      reason: 'Conflict of interest: Administrators cannot approve or moderate their own property listings.',
    };
  }

  if (hasAdminPermission(role, 'listings', 'approve')) {
    return { allowed: true };
  }

  return {
    allowed: false,
    reason: `${role} does not possess listing moderation privileges.`,
  };
}

/**
 * Fetches or creates an administrator profile record from Firestore /admins/{uid}
 * Preserves role assignments saved in Firestore without hardcoded clobbering.
 */
export async function getAdminRecord(uid: string, email?: string): Promise<AdminUser | null> {
  try {
    const adminDocRef = doc(db, 'admins', uid);
    const snap = await getDoc(adminDocRef);

    if (snap.exists()) {
      const data = snap.data() as AdminUser;
      return data;
    }

    // If document doesn't exist yet, seed initial record with appropriate role ONLY if designated admin
    const cleanEmail = (email || auth.currentUser?.email || '').trim().toLowerCase();
    if (!isDesignatedAdminEmail(cleanEmail)) {
      return null;
    }
    const role = getDefaultRoleForEmail(cleanEmail);

    const initialAdmin: AdminUser = {
      uid,
      email: cleanEmail,
      fullName: cleanEmail.split('@')[0].replace(/[._-]/g, ' ').toUpperCase() || 'Administrator',
      role,
      status: 'active',
      twoFactorEnabled: true,
      lastLoginAt: new Date().toISOString(),
      lastActiveAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      backupCodes: generateBackupRecoveryCodes(),
    };

    await setDoc(adminDocRef, initialAdmin, { merge: true });
    return initialAdmin;
  } catch (err) {
    console.warn('getAdminRecord warning:', err);
    return null;
  }
}

/**
 * Loads all administrator records (Restricted to Super Admin)
 */
export async function getAllAdminRecords(): Promise<AdminUser[]> {
  try {
    const adminsCol = collection(db, 'admins');
    const snap = await getDocs(adminsCol);
    const list: AdminUser[] = [];
    snap.forEach((d) => {
      list.push(d.data() as AdminUser);
    });
    return list;
  } catch (err) {
    console.warn('getAllAdminRecords warning:', err);
    return [];
  }
}

/**
 * Updates administrator role or status
 */
export async function updateAdminUserRecord(
  uid: string,
  updates: Partial<AdminUser>
): Promise<boolean> {
  try {
    const ref = doc(db, 'admins', uid);
    await updateDoc(ref, {
      ...updates,
      updatedAt: new Date().toISOString(),
    });
    return true;
  } catch (err) {
    console.error('updateAdminUserRecord error:', err);
    return false;
  }
}

// ==========================================
// TWO-FACTOR AUTHENTICATION (2FA) UTILITIES
// ==========================================

export function generateBackupRecoveryCodes(): string[] {
  const codes: string[] = [];
  const chars = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
  for (let i = 0; i < 6; i++) {
    const bytes = new Uint8Array(8);
    if (typeof window !== 'undefined' && window.crypto) {
      window.crypto.getRandomValues(bytes);
    }
    let p1 = '';
    let p2 = '';
    for (let j = 0; j < 4; j++) p1 += chars[bytes[j] % chars.length];
    for (let j = 4; j < 8; j++) p2 += chars[bytes[j] % chars.length];
    codes.push(`${p1}-${p2}`);
  }
  return codes;
}

export async function hashBackupCode(code: string): Promise<string> {
  const clean = code.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (typeof window !== 'undefined' && window.crypto?.subtle) {
    const data = new TextEncoder().encode(clean);
    const hash = await window.crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, '0')).join('');
  }
  return clean;
}

/**
 * Dispatches an automated 30-second verification code to the specified administrator email.
 */
export async function sendAdminEmailOtp(email: string): Promise<{
  success: boolean;
  error?: string;
  emergencyCode?: string;
  smtpIssue?: boolean;
}> {
  const cleanEmail = email.trim().toLowerCase();
  if (!cleanEmail || !cleanEmail.includes('@')) {
    return { success: false, error: 'Valid administrator email address is required' };
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 12000);

    const res = await fetch(
      'https://us-central1-cameroon-land-market-ef9fd.cloudfunctions.net/sendAdminOtpHttp',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: cleanEmail }),
        signal: controller.signal,
      }
    );
    clearTimeout(timeoutId);

    if (res.ok) {
      const data = await res.json().catch(() => ({}));
      return {
        success: true,
        emergencyCode: data.emergencyCode,
        smtpIssue: Boolean(data.smtpIssue),
      };
    } else {
      const errData = await res.json().catch(() => ({}));
      return { success: false, error: errData.error || 'Failed to dispatch security email' };
    }
  } catch (err: any) {
    console.error('sendAdminEmailOtp failed:', err);
    return { success: false, error: err.message || 'Error sending security code' };
  }
}

/**
 * Validates a 6-digit OTP code against the backend 30s verification endpoint.
 * Requires an active Firebase ID token and strictly fails closed without client fallback.
 */
export async function verifyAdminEmailOtp(
  email: string,
  code: string
): Promise<{ valid: boolean; error?: string }> {
  const cleanEmail = email.trim().toLowerCase();
  const cleanCode = code.trim().replace(/\s+/g, '');

  if (!cleanCode) {
    return { valid: false, error: 'Veuillez saisir le code de vérification' };
  }

  try {
    const idToken = await auth.currentUser?.getIdToken(true);
    if (!idToken) {
      return { valid: false, error: 'Session non authentifiée. Veuillez vous reconnecter.' };
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 12000);

    const res = await fetch(
      'https://us-central1-cameroon-land-market-ef9fd.cloudfunctions.net/verifyAdminOtpHttp',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ email: cleanEmail, code: cleanCode }),
        signal: controller.signal,
      }
    );
    clearTimeout(timeoutId);

    const data = await res.json().catch(() => ({}));
    if (res.ok && data.valid) {
      // Force refresh user token to activate newly set adminMfaAt custom claim
      await auth.currentUser?.getIdToken(true);
      return { valid: true };
    } else {
      return { valid: false, error: data.error || 'Code de vérification invalide ou expiré.' };
    }
  } catch (err: any) {
    console.error('verifyAdminEmailOtp error:', err);
    // Strict fail closed: no client-side fallback
    return { valid: false, error: err.message || 'Impossible de joindre le serveur de vérification 2FA.' };
  }
}

// ==========================================
// SESSION INACTIVITY WATCHDOG
// ==========================================



const DEFAULT_INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 15 Minutes

export class SessionInactivityWatchdog {
  private timeoutId: any = null;
  private onTimeoutCallback: () => void;
  private timeoutDurationMs: number;
  private isListening = false;

  constructor(onTimeout: () => void, durationMs = DEFAULT_INACTIVITY_TIMEOUT_MS) {
    this.onTimeoutCallback = onTimeout;
    this.timeoutDurationMs = durationMs;
  }

  public start() {
    this.reset();
    if (!this.isListening && typeof window !== 'undefined') {
      const resetHandler = () => this.reset();
      window.addEventListener('mousemove', resetHandler, { passive: true });
      window.addEventListener('keydown', resetHandler, { passive: true });
      window.addEventListener('touchstart', resetHandler, { passive: true });
      window.addEventListener('scroll', resetHandler, { passive: true });
      this.isListening = true;
    }
  }

  public reset() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.timeoutId = setTimeout(() => {
      this.onTimeoutCallback();
    }, this.timeoutDurationMs);
  }

  public stop() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
}

// ==========================================
// AUTHENTICATION RATE LIMITER
// ==========================================

const RATE_LIMIT_PREFIX = 'clm_admin_rl_';
const MAX_FAILED_ATTEMPTS = 5;
const LOCKOUT_WINDOW_MS = 15 * 60 * 1000; // 15 minutes

export interface RateLimitCheck {
  allowed: boolean;
  remainingAttempts: number;
  attemptsRemaining?: number;
  lockoutSecondsRemaining?: number;
  lockoutMinutes?: number;
  message?: string;
}

const memoryRateLimitStore: Record<string, string> = {};

function getRateLimitStorage() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
    if (typeof globalThis !== 'undefined' && (globalThis as any).localStorage) return (globalThis as any).localStorage;
  } catch {}
  return {
    getItem: (k: string) => memoryRateLimitStore[k] || null,
    setItem: (k: string, v: string) => { memoryRateLimitStore[k] = v; },
    removeItem: (k: string) => { delete memoryRateLimitStore[k]; },
  };
}

export function checkLoginRateLimit(identifier: string): RateLimitCheck {
  const storage = getRateLimitStorage();
  const cleanKey = RATE_LIMIT_PREFIX + identifier.trim().toLowerCase().replace(/[^a-z0-9]/g, '_');
  try {
    const raw = storage.getItem(cleanKey);
    if (!raw) return { allowed: true, remainingAttempts: MAX_FAILED_ATTEMPTS, attemptsRemaining: MAX_FAILED_ATTEMPTS };
    const { attempts, lockedUntil } = JSON.parse(raw);
    const now = Date.now();
    if (lockedUntil && now < lockedUntil) {
      const lockoutSecondsRemaining = Math.ceil((lockedUntil - now) / 1000);
      const lockoutMinutes = Math.ceil(lockoutSecondsRemaining / 60);
      return {
        allowed: false,
        remainingAttempts: 0,
        attemptsRemaining: 0,
        lockoutSecondsRemaining,
        lockoutMinutes,
        message: `Too many failed attempts. Account temporarily locked for security. Please try again in ${lockoutMinutes} minute(s).`,
      };
    }
    // If lockout expired, reset
    if (lockedUntil && now >= lockedUntil) {
      storage.removeItem(cleanKey);
      return { allowed: true, remainingAttempts: MAX_FAILED_ATTEMPTS, attemptsRemaining: MAX_FAILED_ATTEMPTS };
    }
    const remaining = Math.max(0, MAX_FAILED_ATTEMPTS - (attempts || 0));
    return {
      allowed: remaining > 0,
      remainingAttempts: remaining,
      attemptsRemaining: remaining,
    };
  } catch {
    return { allowed: true, remainingAttempts: MAX_FAILED_ATTEMPTS, attemptsRemaining: MAX_FAILED_ATTEMPTS };
  }
}

export function recordFailedLoginAttempt(identifier: string): RateLimitCheck {
  const storage = getRateLimitStorage();
  const cleanKey = RATE_LIMIT_PREFIX + identifier.trim().toLowerCase().replace(/[^a-z0-9]/g, '_');
  try {
    const raw = storage.getItem(cleanKey);
    let attempts = 0;
    if (raw) {
      const parsed = JSON.parse(raw);
      attempts = parsed.attempts || 0;
    }
    attempts += 1;
    let lockedUntil: number | null = null;
    if (attempts >= MAX_FAILED_ATTEMPTS) {
      lockedUntil = Date.now() + LOCKOUT_WINDOW_MS;
    }
    storage.setItem(cleanKey, JSON.stringify({ attempts, lockedUntil, lastAttempt: Date.now() }));
    return checkLoginRateLimit(identifier);
  } catch {
    return { allowed: true, remainingAttempts: 1, attemptsRemaining: 1 };
  }
}

export function resetLoginRateLimit(identifier: string): void {
  const storage = getRateLimitStorage();
  const cleanKey = RATE_LIMIT_PREFIX + identifier.trim().toLowerCase().replace(/[^a-z0-9]/g, '_');
  try {
    storage.removeItem(cleanKey);
  } catch {}
}
